import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HomePage.css';

const OnboardingGuide: React.FC = () => {
  const navigate = useNavigate();
  const [expandedStep, setExpandedStep] = useState<number | null>(1);

  const toggleStep = (num: number) => {
    setExpandedStep(expandedStep === num ? null : num);
  };

  return (
    <div className="home-page">
      <div className="home-header">
        <div className="home-logo">🐴 HeyFilly — Setup Guide</div>
        <div className="home-actions">
          <button className="home-btn home-btn-secondary" onClick={() => navigate('/')}>← Back to Home</button>
        </div>
      </div>

      <div className="home-welcome">
        <h1>🚀 Set Up HeyFilly for Your Organization</h1>
        <p>Follow these steps to connect your Asana project and start collecting feature requests via AI chatbot.</p>
        <p style={{ color: '#718096', fontSize: '14px' }}>Click each step to expand the detailed instructions. ~60 minutes total.</p>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 32px 48px' }}>

        {/* STEP 1 */}
        <StepCard num={1} title="Prepare Your Asana Project" time="10 min" icon="📋" expanded={expandedStep === 1} onToggle={() => toggleStep(1)}>
          <p><strong>You need an Asana project with custom fields.</strong> This is where HeyFilly will create tasks when users submit feature requests.</p>
          
          <h4>1a. Create an Asana Project</h4>
          <p>If you don't have one yet, create a new project in Asana (Board or List view — either works).</p>
          
          <h4>1b. Add Custom Fields</h4>
          <p>Add these custom fields to your Asana project. HeyFilly will write data to these fields:</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '16px' }}>
            <thead>
              <tr style={{ background: '#f7fafc', textAlign: 'left' }}>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Field Name</th>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Type in Asana</th>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Purpose</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Problem Statement', 'Text', 'What problem does this solve?'],
                ['Desired Outcome', 'Text', 'What is the expected result?'],
                ['Business Impact', 'Text', 'How does this impact the business?'],
                ['Annualized Entitlement', 'Number', 'Dollar value of annual savings/revenue'],
                ['Impact Metrics', 'Single-select', 'How success is measured (DEA, Speed, etc.)'],
                ['Theme', 'Single-select', 'Strategic theme'],
                ['Program Name', 'Single-select', 'Which program this belongs to'],
                ['OP Decision', 'Single-select', 'Priority: P1-ATL, P2-ATL, P3-BTL, P4-BTL, New'],
                ['Target Year', 'Text', '2026, 2027, etc.'],
                ['Request Type', 'Single-select', 'OP or In-Year'],
                ['Status', 'Single-select', 'In Review, Not Started, In Progress, etc.'],
                ['Created By', 'People', 'Who submitted the request'],
                ['Created At', 'Date', 'When the request was created'],
              ].map(([name, type, purpose], i) => (
                <tr key={i}>
                  <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', fontWeight: 600 }}>{name}</td>
                  <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>{type}</td>
                  <td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>{purpose}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ color: '#718096', fontSize: '13px' }}>You can add more or fewer fields — HeyFilly adapts to whatever custom fields your project has.</p>

          <h4>1c. Get Your Asana Personal Access Token (PAT)</h4>
          <ol>
            <li>Go to <strong>https://app.asana.com/0/my-apps</strong></li>
            <li>Click <strong>"Create new token"</strong></li>
            <li>Name it <code>HeyFilly</code> and click <strong>Create</strong></li>
            <li><strong>Copy the token immediately</strong> — it starts with <code>1/</code> and won't be shown again</li>
          </ol>

          <h4>1d. Get Your Asana Project ID</h4>
          <ol>
            <li>Open your Asana project in a browser</li>
            <li>Look at the URL: <code>https://app.asana.com/0/<strong>1234567890123</strong>/list</code></li>
            <li>The number after <code>/0/</code> is your Project ID</li>
          </ol>
          <div style={{ background: '#ebf4ff', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#2b6cb0' }}>
            ✅ You now have: (1) An Asana project with custom fields, (2) A PAT token, (3) A Project ID. Keep these handy.
          </div>
        </StepCard>

        {/* STEP 2 */}
        <StepCard num={2} title="Create Your Organization in HeyFilly" time="2 min" icon="🏢" expanded={expandedStep === 2} onToggle={() => toggleStep(2)}>
          <ol>
            <li>Go to the <strong>Home Page</strong> (click "← Back to Home" above)</li>
            <li>Click <strong>"+ New Org"</strong> button (top-right) or the dashed card</li>
            <li>Enter your <strong>Organization Name</strong> (e.g., "AFT Outbound", "Transportation", "Learning")</li>
            <li>Click <strong>Create</strong></li>
            <li>You'll land on the <strong>Organization Settings</strong> page</li>
          </ol>
          <div style={{ background: '#ebf4ff', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#2b6cb0' }}>
            💡 The organization name is what your team sees on the home page. Choose something recognizable.
          </div>
        </StepCard>

        {/* STEP 3 */}
        <StepCard num={3} title="Configure Admins" time="1 min" icon="👤" expanded={expandedStep === 3} onToggle={() => toggleStep(3)}>
          <p>You're now on the <strong>Organization Settings</strong> page, <strong>👤 Admins</strong> tab.</p>
          <ol>
            <li><strong>Primary Admin</strong> — Your email is auto-filled. This person has full access to all settings.</li>
            <li><strong>Secondary Admin</strong> — Enter a backup admin's email (optional but recommended)</li>
            <li>Click <strong>💾 Save All Settings</strong></li>
          </ol>
          <div style={{ background: '#fffbeb', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#975a16' }}>
            ⚠️ Only admins can modify settings. Non-admins can use the chatbot and view tasks but can't change configuration.
          </div>
        </StepCard>

        {/* STEP 4 */}
        <StepCard num={4} title="Connect to Asana (PAT)" time="3 min" icon="🔗" expanded={expandedStep === 4} onToggle={() => toggleStep(4)}>
          <ol>
            <li>Click the <strong>🔗 Asana</strong> tab</li>
            <li>Paste your PAT (from Step 1c) in the <strong>Personal Access Token</strong> field</li>
            <li>Click <strong>🔗 Test Connection</strong></li>
            <li>You should see: <strong>✅ Connected as "Your Name"</strong></li>
            <li>Click <strong>💾 Save All Settings</strong></li>
          </ol>
          <div style={{ background: '#fff5f5', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#c53030' }}>
            ❌ If you see "401 Unauthorized" → Your PAT is invalid or expired. Go back to Asana and generate a new one.<br/>
            ❌ If you see "Connection error" → Check your network connection.
          </div>
        </StepCard>

        {/* STEP 5 */}
        <StepCard num={5} title="Create a Portfolio" time="2 min" icon="📁" expanded={expandedStep === 5} onToggle={() => toggleStep(5)}>
          <p>A <strong>Portfolio</strong> connects to one Asana project. You can have multiple portfolios per org.</p>
          <ol>
            <li>Click the <strong>📁 Portfolios</strong> tab (or go back to the Portfolios page and click the "+" card)</li>
            <li>Click <strong>+ Add Portfolio</strong></li>
            <li>Fill in:
              <ul>
                <li><strong>Portfolio Name</strong>: e.g., "AFT Outbound Backlog"</li>
                <li><strong>Asana Project ID</strong>: Paste the ID from Step 1d (e.g., <code>1234567890123</code>)</li>
                <li><strong>Inherit organization settings</strong>: Leave <strong>ON</strong></li>
              </ul>
            </li>
            <li>Click <strong>Create Portfolio</strong></li>
          </ol>
        </StepCard>

        {/* STEP 6 */}
        <StepCard num={6} title="Import Field Mappings from Asana" time="3 min" icon="📥" expanded={expandedStep === 6} onToggle={() => toggleStep(6)}>
          <p>This step auto-populates the field mapping table with your Asana project's custom fields.</p>
          <ol>
            <li>Click the <strong>🗂️ Field Mappings</strong> tab</li>
            <li>Click <strong>📥 Import from Asana</strong></li>
            <li>A dialog shows: "Found X custom fields. Replace current mappings?"</li>
            <li>Click <strong>OK</strong></li>
            <li>The table now shows your Asana custom fields with their GIDs auto-populated</li>
            <li>Click <strong>💾 Save All Settings</strong></li>
          </ol>
          <div style={{ background: '#ebf4ff', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#2b6cb0' }}>
            💡 Import fetches field names, GIDs, and types automatically. You still need to set the <strong>Source</strong> for each field (next step).
          </div>
        </StepCard>

        {/* STEP 7 */}
        <StepCard num={7} title="Configure Field Sources" time="10 min" icon="🗂️" expanded={expandedStep === 7} onToggle={() => toggleStep(7)}>
          <p>Each field needs a <strong>Source</strong> — this tells HeyFilly where the value comes from:</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '16px' }}>
            <thead>
              <tr style={{ background: '#f7fafc', textAlign: 'left' }}>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Source</th>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Icon</th>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Meaning</th>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Example Fields</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>iris</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>🤖</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>AI extracts from conversation</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>problem_statement, desired_outcome, business_impact, theme, program_name</td></tr>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>prompt</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>💬</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>Asked to user as a question</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>request_type, annualized_entitlement, impact_metrics</td></tr>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>system</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>⚙️</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>Auto-set by the system</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>created_at, created_by, op_decision, target_year</td></tr>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>manual</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>📝</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>Set by PM in Task Prioritization</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>hc_sde, l7_manager, status, launch_date, rationale</td></tr>
            </tbody>
          </table>
          <p><strong>How to set:</strong></p>
          <ol>
            <li>Click <strong>✏️</strong> on a field row to enter edit mode</li>
            <li>Change the <strong>Source</strong> dropdown to the correct value</li>
            <li>Set <strong>Write?</strong> checkbox: ✅ for fields written on task creation, ❌ for read-only fields</li>
            <li>Click <strong>✅</strong> to save the row</li>
            <li>Repeat for all fields</li>
            <li>Click <strong>💾 Save All Settings</strong></li>
          </ol>
        </StepCard>

        {/* STEP 8 */}
        <StepCard num={8} title="Configure Prompt Questions" time="10 min" icon="💬" expanded={expandedStep === 8} onToggle={() => toggleStep(8)}>
          <p>For fields with Source = <strong>prompt</strong>, you configure the question shown to users after the AI conversation.</p>
          <ol>
            <li>Find a field with Source = <strong>💬 prompt</strong></li>
            <li>Click the <strong>⚙️</strong> button (only appears on prompt fields)</li>
            <li>Fill in the prompt config:</li>
          </ol>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '16px' }}>
            <thead>
              <tr style={{ background: '#f7fafc', textAlign: 'left' }}>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Setting</th>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Description</th>
                <th style={{ padding: '8px', border: '1px solid #e2e8f0' }}>Example</th>
              </tr>
            </thead>
            <tbody>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>Question</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>What to ask the user</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>"What type of request is this?"</td></tr>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>Input Type</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>How to collect the answer</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>chips, dropdown, currency, text, number, date</td></tr>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>Options</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>For chips/dropdown only</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>"OP Request, In-Year"</td></tr>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>Order</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>Position in the flow (1 = first)</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>1, 2, 3</td></tr>
              <tr><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}><strong>Required</strong></td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0' }}>Must answer to proceed</td><td style={{ padding: '6px 8px', border: '1px solid #e2e8f0', color: '#718096' }}>✅ Yes</td></tr>
            </tbody>
          </table>
          <p><strong>Input type guide:</strong></p>
          <ul style={{ fontSize: '13px', lineHeight: 1.8 }}>
            <li><code>chips</code> — Clickable buttons. Best for 2-7 options.</li>
            <li><code>dropdown</code> — Select menu. Best for 8+ options.</li>
            <li><code>currency</code> — Dollar presets ($100K, $500K, $1M) + custom input.</li>
            <li><code>text</code> — Free text. User types and presses Enter.</li>
            <li><code>number</code> — Numeric input.</li>
            <li><code>date</code> — Calendar date picker.</li>
          </ul>
          <ol start={4}>
            <li>Click <strong>Done</strong></li>
            <li>Click <strong>💾 Save All Settings</strong></li>
          </ol>
        </StepCard>

        {/* STEP 9 */}
        <StepCard num={9} title="Configure IRIS Agents" time="15 min" icon="🤖" expanded={expandedStep === 9} onToggle={() => toggleStep(9)}>
          <p>HeyFilly uses two AI agents. <strong>If you're using the defaults, skip this step.</strong></p>
          
          <h4>Chat Agent — Conducts the conversation</h4>
          <p>This agent asks the user questions to gather feature request details.</p>
          <p><strong>To use your own agent:</strong></p>
          <ol>
            <li>Create an agent in IRIS/Bedrock with this prompt:</li>
          </ol>
          <pre style={{ background: '#1a202c', color: '#e2e8f0', padding: '16px', borderRadius: '8px', fontSize: '12px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{`You are Filly, a friendly AI assistant that helps users submit feature requests.

Your job is to gather the following information through natural conversation:
- Problem Statement: What problem does this solve?
- Desired Outcome: What's the expected result?
- Business Impact: How does this impact the business?
- Stakeholders: Who is affected?

Guidelines:
- Be conversational and friendly
- Ask one question at a time
- When you have all the information, respond with "✅ All fields complete"
- Do NOT generate JSON`}</pre>
          <ol start={2}>
            <li>Copy the <strong>Agent ID</strong> (UUID)</li>
            <li>Go to <strong>Organization Settings → 🤖 Agents → Chat Agent ID</strong></li>
            <li>Paste the UUID and click <strong>💾 Save All Settings</strong></li>
          </ol>

          <h4 style={{ marginTop: '24px' }}>FinalJSON Agent — Generates structured data</h4>
          <p>This agent converts the conversation into a JSON object.</p>
          <pre style={{ background: '#1a202c', color: '#e2e8f0', padding: '16px', borderRadius: '8px', fontSize: '12px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{`You are a JSON extraction agent. When the user says "Generate JSON", output ONLY a valid JSON object:

{
  "title": "Brief title",
  "problem_statement": "The problem described",
  "desired_outcome": "What the user wants",
  "business_impact": "Business impact",
  "stakeholders": "Who is affected",
  "theme": "Best matching theme from PROGRAM_LIST",
  "program_name": "Best matching program from PROGRAM_LIST",
  "assigned_team": "Team name if mentioned",
  "conversation_summary": "Brief summary"
}

Rules:
- Output ONLY JSON — no markdown, no explanation
- Field names must match exactly
- Empty string for missing fields`}</pre>
          <div style={{ background: '#fffbeb', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#975a16', marginTop: '12px' }}>
            ⚠️ <strong>Important:</strong> The field names in the JSON output (e.g., <code>problem_statement</code>) must match the <code>fieldName</code> values in your Field Mappings table.
          </div>
        </StepCard>

        {/* STEP 10 */}
        <StepCard num={10} title="Test End-to-End" time="5 min" icon="🧪" expanded={expandedStep === 10} onToggle={() => toggleStep(10)}>
          <ol>
            <li>Go to <strong>Home Page</strong> → Click your organization card</li>
            <li>You're now in the chatbot</li>
            <li>Type: <em>"We need to improve the pick rate at SDC sites. Operators spend too much time walking between aisles. This could save $2M annually."</em></li>
            <li>Answer the AI's follow-up questions</li>
            <li>After the summary, answer the <strong>structured capture questions</strong> (your configured prompts)</li>
            <li>Review the <strong>Editable Summary Card</strong> — edit any field if needed</li>
            <li>Click <strong>✅ Submit to Asana</strong></li>
            <li>Check your Asana project — the task should appear with all custom fields populated</li>
          </ol>
          <div style={{ background: '#fff5f5', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#c53030' }}>
            <strong>If submission fails:</strong><br/>
            • PAT expired → Regenerate in Asana and update in Settings<br/>
            • Field GID mismatch → Re-import from Asana (Step 6)<br/>
            • Enum option name mismatch → Ensure option names match exactly between HeyFilly and Asana
          </div>
        </StepCard>

        {/* STEP 11 */}
        <StepCard num={11} title="Add More Portfolios (Optional)" time="5 min" icon="➕" expanded={expandedStep === 11} onToggle={() => toggleStep(11)}>
          <p>If your org has multiple Asana projects, create additional portfolios:</p>
          <ol>
            <li>Go to <strong>Organization Settings → 📁 Portfolios</strong></li>
            <li>Click <strong>+ Add Portfolio</strong></li>
            <li>Enter the new portfolio's name and Asana Project ID</li>
            <li><strong>Inherit ON</strong> = uses same PAT, agents, field mappings as the org</li>
            <li><strong>Inherit OFF</strong> = you can set different PAT, agents, and field mappings for this portfolio</li>
          </ol>
        </StepCard>

        {/* STEP 12 */}
        <StepCard num={12} title="Invite Your Team" time="2 min" icon="👥" expanded={expandedStep === 12} onToggle={() => toggleStep(12)}>
          <p>Share the URL with your team: <strong>https://heyfilly.aft.amazon.dev</strong></p>
          <p>Team members will:</p>
          <ol>
            <li>See the Home Page with your organization card</li>
            <li>Click the card to enter the chatbot</li>
            <li>Submit feature requests — <strong>no setup needed on their end</strong></li>
          </ol>
          <div style={{ background: '#f0fff4', padding: '12px', borderRadius: '8px', fontSize: '13px', color: '#276749' }}>
            🎉 <strong>You're done!</strong> Your team can now submit feature requests via AI chatbot, and they'll appear in your Asana project with all custom fields populated.
          </div>
        </StepCard>

      </div>
    </div>
  );
};

/** Reusable expandable step card */
const StepCard: React.FC<{
  num: number;
  title: string;
  time: string;
  icon: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ num, title, time, icon, expanded, onToggle, children }) => (
  <div style={{
    marginBottom: '12px',
    background: 'white',
    borderRadius: '12px',
    border: expanded ? '2px solid #3182ce' : '1px solid #e2e8f0',
    overflow: 'hidden',
  }}>
    <div
      onClick={onToggle}
      style={{
        display: 'flex',
        gap: '16px',
        padding: '16px 20px',
        cursor: 'pointer',
        alignItems: 'center',
        background: expanded ? '#ebf8ff' : 'transparent',
      }}
    >
      <div style={{
        minWidth: '40px',
        height: '40px',
        borderRadius: '50%',
        background: expanded ? '#3182ce' : '#ebf4ff',
        color: expanded ? 'white' : '#3182ce',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontWeight: 700,
      }}>
        {num}
      </div>
      <div style={{ flex: 1 }}>
        <h3 style={{ margin: 0, fontSize: '15px', color: '#1a202c' }}>
          {icon} {title}
        </h3>
      </div>
      <span style={{ fontSize: '12px', color: '#a0aec0' }}>{time}</span>
      <span style={{ fontSize: '18px', color: '#a0aec0' }}>{expanded ? '▼' : '▶'}</span>
    </div>
    {expanded && (
      <div style={{ padding: '0 20px 20px 76px', fontSize: '14px', color: '#4a5568', lineHeight: 1.7 }}>
        {children}
      </div>
    )}
  </div>
);

export default OnboardingGuide;